import {
  INTEGRATIONS_PATH,
  OWN_PATH_GUIDES,
  PLATFORMS_PATH,
  PROCESSORS_PATH,
  SITE_PAGES,
  type SitePage
} from "./constants"
import type { ApiPlatform } from "../../docs/types"

export type ParsedLocation = {
  page: SitePage
  guideSlug: string | null
  // /integrations/<id>; null on every other page.
  frameworkId: string | null
  // /platforms/<id>; null on the index and every other page.
  platformId: string | null
  // /processors/<id>; null on the index and every other page.
  processorId: string | null
  apiPlatform: "all" | ApiPlatform
  docsQuery: string
  hash: string
}

const API_PLATFORMS = new Set<ApiPlatform>([
  "node",
  "rails",
  "http",
  "browser",
  "cli",
  "host"
])

const SLUG = "[a-z0-9]+(?:-[a-z0-9]+)*"

export const parsePathname = (
  pathname: string
): Pick<ParsedLocation, "page" | "guideSlug" | "frameworkId" | "platformId" | "processorId"> => {
  const path = pathname.replace(/\/+$/, "") || "/"
  if (path === "/api_docs" || path === "/guides/api-reference") {
    return { page: SITE_PAGES.API_DOCS, guideSlug: null, frameworkId: null, platformId: null, processorId: null }
  }
  if (path === "/guides") return { page: SITE_PAGES.GUIDES, guideSlug: null, frameworkId: null, platformId: null, processorId: null }
  // /agents and /btcpay: guides the contract publishes at their own path.
  const ownPath = Object.entries(OWN_PATH_GUIDES).find(([, ownPathOf]) => ownPathOf === path)
  if (ownPath) return { page: SITE_PAGES.GUIDES, guideSlug: ownPath[0], frameworkId: null, platformId: null, processorId: null }
  const guideMatch = path.match(new RegExp(`^/guides/(${SLUG})$`))
  if (guideMatch) return { page: SITE_PAGES.GUIDES, guideSlug: guideMatch[1], frameworkId: null, platformId: null, processorId: null }
  const frameworkMatch = path.match(new RegExp(`^${INTEGRATIONS_PATH}/(${SLUG})$`))
  if (frameworkMatch) {
    return { page: SITE_PAGES.INTEGRATION, guideSlug: null, frameworkId: frameworkMatch[1], platformId: null, processorId: null }
  }
  if (path === PLATFORMS_PATH) {
    return { page: SITE_PAGES.PLATFORMS, guideSlug: null, frameworkId: null, platformId: null, processorId: null }
  }
  const platformMatch = path.match(new RegExp(`^${PLATFORMS_PATH}/(${SLUG})$`))
  if (platformMatch) {
    return { page: SITE_PAGES.PLATFORMS, guideSlug: null, frameworkId: null, platformId: platformMatch[1], processorId: null }
  }
  if (path === PROCESSORS_PATH) {
    return { page: SITE_PAGES.PROCESSORS, guideSlug: null, frameworkId: null, platformId: null, processorId: null }
  }
  const processorMatch = path.match(new RegExp(`^${PROCESSORS_PATH}/(${SLUG})$`))
  if (processorMatch) {
    return { page: SITE_PAGES.PROCESSORS, guideSlug: null, frameworkId: null, platformId: null, processorId: processorMatch[1] }
  }
  return { page: SITE_PAGES.HOME, guideSlug: null, frameworkId: null, platformId: null, processorId: null }
}

export const parseLocation = (url: URL = new URL(window.location.href)): ParsedLocation => {
  const { page, guideSlug, frameworkId, platformId, processorId } = parsePathname(url.pathname)
  const platformParam = url.searchParams.get("platform")
  const apiPlatform =
    page === SITE_PAGES.API_DOCS && platformParam && API_PLATFORMS.has(platformParam as ApiPlatform)
      ? (platformParam as ApiPlatform)
      : "all"
  const hash = url.hash.startsWith("#") ? url.hash.slice(1) : url.hash

  return {
    page,
    guideSlug,
    frameworkId,
    platformId,
    processorId,
    apiPlatform,
    docsQuery: url.searchParams.get("q") ?? "",
    hash
  }
}

export const buildPath = (location: Partial<ParsedLocation> & { page: SitePage }): string => {
  const url = new URL("https://openreceive.org")
  if (location.page === SITE_PAGES.API_DOCS) url.pathname = "/api_docs"
  else if (location.page === SITE_PAGES.GUIDES) {
    url.pathname = location.guideSlug
      ? (OWN_PATH_GUIDES[location.guideSlug] ?? `/guides/${location.guideSlug}`)
      : "/guides"
  } else if (location.page === SITE_PAGES.INTEGRATION && location.frameworkId) {
    url.pathname = `${INTEGRATIONS_PATH}/${location.frameworkId}`
  } else if (location.page === SITE_PAGES.PLATFORMS) {
    url.pathname = location.platformId ? `${PLATFORMS_PATH}/${location.platformId}` : PLATFORMS_PATH
  } else if (location.page === SITE_PAGES.PROCESSORS) {
    url.pathname = location.processorId ? `${PROCESSORS_PATH}/${location.processorId}` : PROCESSORS_PATH
  } else url.pathname = "/"

  if (location.page === SITE_PAGES.API_DOCS && location.apiPlatform && location.apiPlatform !== "all") {
    url.searchParams.set("platform", location.apiPlatform)
  }
  if (location.docsQuery?.trim()) url.searchParams.set("q", location.docsQuery.trim())
  if (location.hash) url.hash = location.hash

  return `${url.pathname}${url.search}${url.hash}`
}

export const splitHref = (href: string): { path: string; hash: string } => {
  const [path, hash = ""] = href.split("#")
  return { path, hash }
}
